import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Snackbar, Paper, Slide, IconButton, Box, Typography } from '@mui/material';
import { Close as CloseIcon, Forum as ForumIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import { notificationHtmlToText } from '../utils/formatters';
import DiscordAlertsWidget from '../components/DiscordAlertsWidget';

const NotificationContext = createContext(null);

const POLL_INTERVAL = 15000; // 15 seconds

function SlideUp(props) {
  return <Slide {...props} direction="up" />;
}

export const NotificationProvider = ({ children }) => {
  const [toast, setToast] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastSeenIdRef = useRef(null);
  const firstPollRef = useRef(true);
  const visibleRef = useRef(true);

  // Track page visibility — don't show browser notifications when tab is focused
  useEffect(() => {
    const handleVisibility = () => {
      visibleRef.current = !document.hidden;
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Request browser notification permission on mount
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const pollNotifications = useCallback(async () => {
    try {
      const params = lastSeenIdRef.current
        ? `?afterId=${encodeURIComponent(lastSeenIdRef.current)}`
        : '';
      const resp = await apiRequest(`/api/notifications/recent${params}`);
      const notifications = resp.data?.notifications || [];

      if (notifications.length === 0) return;

      // Update last seen ID
      lastSeenIdRef.current = notifications[notifications.length - 1].id;

      // On first poll, just set the cursor without showing toasts (avoid spam on load)
      if (firstPollRef.current) {
        firstPollRef.current = false;
        return;
      }

      // Add all notifications to the alerts widget (trade_alerts, price_alerts, etc.)
      if (notifications.length > 0) {
        setAlerts(prev => [...prev, ...notifications].slice(-50));
        setUnreadCount(prev => prev + notifications.length);
      }

      // Show toast + browser notification for each new notification
      for (const notif of notifications) {
        const isTradeAlert = notif.type === 'trade_alert';

        // MUI Snackbar toast (always show, even when tab is focused)
        setToast({
          id: notif.id,
          title: notif.title,
          body: notif.body,
          type: notif.type,
          channelName: notif.channelName,
        });

        // Browser notification (only when tab is NOT visible)
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && !visibleRef.current) {
          try {
            new Notification(notif.title, {
              body: notificationHtmlToText(notif.body),
              icon: isTradeAlert ? undefined : undefined,
              tag: notif.id,
            });
          } catch (e) {
            // Some browsers throw if notification is created too quickly
          }
        }
      }
    } catch (err) {
      // Silently fail — don't spam console with polling errors
    }
  }, []);

  useEffect(() => {
    // Initial poll after 3 seconds (let app boot)
    const initialTimer = setTimeout(() => {
      // On first poll, just set the cursor — don't show old notifications
      apiRequest('/api/notifications/recent')
        .then(resp => {
          const notifications = resp.data?.notifications || [];
          if (notifications.length > 0) {
            lastSeenIdRef.current = notifications[notifications.length - 1].id;
          }
        })
        .catch(() => {});
      firstPollRef.current = true;

      // Start regular polling
      const interval = setInterval(pollNotifications, POLL_INTERVAL);
      pollIntervalRef.current = interval;
    }, 3000);

    const pollIntervalRef = { current: null };

    return () => {
      clearTimeout(initialTimer);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [pollNotifications]);

  const handleClose = () => setToast(null);

  const clearUnread = useCallback(() => setUnreadCount(0), []);
  const clearAlerts = useCallback(() => setAlerts([]), []);

  return (
    <NotificationContext.Provider value={{ unreadCount, clearUnread, alerts }}>
      {children}
      <DiscordAlertsWidget
        alerts={alerts}
        unreadCount={unreadCount}
        onClear={clearAlerts}
        onMarkRead={clearUnread}
      />
      <Snackbar
        open={!!toast}
        autoHideDuration={8000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={SlideUp}
      >
        {toast ? (
          <Paper
            elevation={8}
            sx={{
              width: '100%',
              maxWidth: 400,
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            {toast.type === 'trade_alert'
              ? <TrendingUpIcon fontSize="small" sx={{ color: 'success.main', mt: 0.25, flexShrink: 0 }} />
              : <ForumIcon fontSize="small" sx={{ color: 'info.main', mt: 0.25, flexShrink: 0 }} />}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
                {toast.title}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25, wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                {notificationHtmlToText(toast.body)}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleClose} sx={{ p: 0.25, flexShrink: 0 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Paper>
        ) : undefined}
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);

export default NotificationProvider;
